import type { SpecBillingSummary } from '@parcel/types';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@parcel/ui';
import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';
import {
  SpecBillingError,
  specBillingSaveMessage,
  staleAssignmentSet,
  updateSpecSeatAssignments,
} from '../../lib/spec-billing.js';

/**
 * One member's seat tier: the picker in the Members table's `Tier` column.
 *
 * A named choice rather than an unlabeled switch. A switch states one option
 * and leaves the other implicit, so the off position had to be read as "not
 * the thing the column is named after" -- and the column was named `AI seat`,
 * which made the common case the negation of the rare one. Two rows on a menu
 * name both, and the trigger reports which one is in force without anyone
 * having to decode a toggle.
 *
 * Assigns an ALREADY-PAID seat and can NEVER buy one. Buying seats is a
 * Billing action; this control only ever writes the ASSIGNMENT set, never the
 * purchased quantity, and disables the `AI seat` row instead of sending a
 * write the server can only refuse -- assigning when none remain 409s
 * `spec_assignment_limit`.
 *
 * `updateSpecSeatAssignments` replaces the WHOLE assignment set
 * (spec-billing.ts), so a write here needs every OTHER member already holding
 * a seat, not just this row's id. `assignedMemberIds` carries that: it is the
 * same `summary.assignedMemberIds` the Members page already holds from its one
 * `getSpecBilling` read, passed straight through rather than refetched per row.
 *
 * ONE write in flight for the WHOLE column, not one per row. The assignment
 * route replaces the whole set from whatever list a request carries, and the
 * only concurrency control on the server is a workspace-wide transaction lock,
 * which orders two requests but cannot tell that the second one's input
 * predates the first one's result. Two rows writing concurrently each read the
 * summary the PAGE held before either write landed, so whichever request lands
 * second silently replaces the set the first one just confirmed -- and BOTH
 * requests are individually valid, so neither is refused on capacity.
 * `writeInFlight` -- true for every row the instant ANY row starts a write,
 * not just its own -- closes this by making "one seat-assignment write at a
 * time" the enforced granularity, the same granularity the route's own
 * whole-set-replace contract already assumes.
 *
 * That serialization only reaches THIS tab, so every write also carries
 * `expectedMemberIds`: the set the payload was built from. A second tab, or a
 * second admin, moving the set out from under this one is refused server-side
 * with 409 `spec_assignment_stale` rather than applied, and the refusal
 * carries the current set so the column resyncs instead of inviting a retry
 * that would be refused again for the same reason.
 */

/** What a member's seat entitles them to. Exactly two, and never a null state:
 *  every member holds a workspace seat, so the question is only which kind. */
export type SpecSeatTier = 'standard' | 'ai';

const TIER_LABELS: Record<SpecSeatTier, string> = {
  standard: 'Standard seat',
  ai: 'AI seat',
};

const TIER_DESCRIPTIONS: Record<SpecSeatTier, string> = {
  standard: 'No Spec access',
  ai: 'Can use Spec',
};

const TIERS = ['standard', 'ai'] as const;

export interface SpecSeatTierSelectProps {
  /** Workspace UUID. The Spec billing routes are keyed by id, not slug. */
  workspaceId: string;
  /** This row's member id (a workspace member id, never an invitation id). */
  memberId: string;
  /**
   * Every member id CURRENTLY holding an AI seat, from the summary the page
   * already holds. `updateSpecSeatAssignments` takes the whole set, so a
   * write here is built from this list with `memberId` added or removed --
   * everyone else's assignment is left exactly as it was.
   */
  assignedMemberIds: readonly string[];
  /** Whether THIS member currently holds an AI seat. */
  assigned: boolean;
  /**
   * Seats still assignable workspace-wide (purchased or pending, minus
   * assigned). Zero disables the `AI seat` row for a member who does not
   * already hold one; it never blocks moving back to `Standard seat`, so a
   * fully assigned workspace can still reassign.
   */
  seatsRemaining: number;
  /** Owner/admin. `false` renders the picker read-only. */
  canManage: boolean;
  /** `false` for a pending invitation: it has no member id an AI seat can hold. */
  memberActive: boolean;
  /**
   * True while ANY row in the column has a write in flight, not just this
   * one. `assignedMemberIds` is a snapshot; a second row writing before the
   * first one's result comes back would build its payload from a set that
   * is already stale the instant it is sent. Disabling every row for the
   * duration of one write is what keeps that snapshot honest.
   */
  writeInFlight: boolean;
  /**
   * Called with `true` the instant this row starts a write and `false` once
   * it settles (success or failure either way), so the page can hold every
   * OTHER row disabled for exactly that long and no longer.
   */
  onWriteInFlightChange: (inFlight: boolean) => void;
  /**
   * Called with the full summary a successful write returns. The page owns
   * the summary; this control keeps no copy of its own past the write that
   * produced it.
   */
  onSummaryChange: (summary: SpecBillingSummary) => void;
  /**
   * Called with the server's CURRENT assignment set when a write is refused
   * as stale. Separate from `onSummaryChange` because a refusal carries the
   * assignment set and nothing else: there is no summary to hand up, and
   * inventing one would put made-up seat counts and balances on the page.
   * The page patches the set it holds and every row re-renders from it.
   */
  onAssignmentsChanged: (assignedMemberIds: readonly string[]) => void;
}

const FALLBACK_ERROR_MESSAGE = 'Unable to change this seat tier.';

export function SpecSeatTierSelect({
  workspaceId,
  memberId,
  assignedMemberIds,
  assigned,
  seatsRemaining,
  canManage,
  memberActive,
  writeInFlight,
  onWriteInFlightChange,
  onSummaryChange,
  onAssignmentsChanged,
}: SpecSeatTierSelectProps) {
  const [tier, setTier] = React.useState<SpecSeatTier>(assigned ? 'ai' : 'standard');
  const [error, setError] = React.useState<string | null>(null);

  // The page hands down a new `assigned` once a write -- this row's own, or
  // another admin's concurrent change -- lands and the summary it holds is
  // replaced. Resyncing to that rather than trusting the last optimistic
  // choice is what lets a second admin's change show up here without a reload.
  React.useEffect(() => {
    setTier(assigned ? 'ai' : 'standard');
  }, [assigned]);

  // Capacity gates the ONE row that would consume a seat, not the whole
  // control: moving back to `Standard seat` is how a full workspace frees one.
  const outOfSeats = !assigned && seatsRemaining <= 0;
  const disabled = !canManage || !memberActive || writeInFlight;

  const select = async (next: SpecSeatTier) => {
    // Belt and braces: a disabled trigger cannot open, and the blocked row
    // cannot be selected. The write below must never run on anything less than
    // the same guards the rendered control itself used.
    if (disabled || next === tier) return;
    if (next === 'ai' && outOfSeats) return;

    setError(null);
    setTier(next);
    onWriteInFlightChange(true);
    const nextMemberIds =
      next === 'ai'
        ? [...assignedMemberIds, memberId]
        : assignedMemberIds.filter((id) => id !== memberId);

    try {
      // The third argument is the fence: `assignedMemberIds` is the snapshot
      // `nextMemberIds` was just derived from, so this adds no read and no new
      // state. Sending the same array the payload came from is the whole
      // point -- anything else would fence against a set this change was not
      // actually built on.
      const summary = await updateSpecSeatAssignments(
        workspaceId,
        nextMemberIds,
        assignedMemberIds,
      );
      onSummaryChange(summary);
    } catch (err) {
      // Revert to the last server-confirmed value. Unlike the Billing seat
      // picker there is no draft to preserve for a retry: one choice is one
      // write, so a rejected write has nothing partial left to keep.
      setTier(assigned ? 'ai' : 'standard');
      if (err instanceof SpecBillingError && err.code === 'spec_assignment_stale') {
        // Distinct from every other failure here: the write did not fail, it
        // was refused because the world moved. The member needs the NEW state
        // and a sentence saying their change was not applied -- not a retry
        // prompt for a request that would be refused again for the same
        // reason. `onAssignmentsChanged` replaces the set the page holds, and
        // the `assigned` effect above re-renders this row from it.
        const current = staleAssignmentSet(err);
        if (current) onAssignmentsChanged(current);
        setError('Seat assignments changed while you were editing. This shows the current seats.');
      } else {
        setError(specBillingSaveMessage(err, FALLBACK_ERROR_MESSAGE));
      }
    } finally {
      onWriteInFlightChange(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <DropdownMenu>
        {/*
          `disabled` on the TRIGGER as well as on the button inside it. Radix
          opens on `pointerdown`, and a disabled `<button>` only stops that
          because shadcn's variants add `disabled:pointer-events-none` -- so
          without this the guard is a CSS rule, and the write serialization
          this column depends on would come apart the moment that class did.
          With it, Radix itself refuses to open.
        */}
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-full justify-between gap-2 px-2 font-normal"
            disabled={disabled}
            // Names the FIELD and its value. The visible text alone would make
            // the accessible name change with the value, so nothing could
            // address this control by a stable name.
            aria-label={`Seat tier: ${TIER_LABELS[tier]}`}
            data-testid="seat-tier"
          >
            <span className="truncate">{TIER_LABELS[tier]}</span>
            <ChevronDown className="size-3.5 shrink-0 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {TIERS.map((option) => {
            const blocked = option === 'ai' && outOfSeats;
            return (
              <DropdownMenuItem
                key={option}
                disabled={blocked}
                onSelect={() => void select(option)}
                className="gap-3"
              >
                <div className="min-w-0 flex-1">
                  <p>{TIER_LABELS[option]}</p>
                  {/*
                    The capacity reason lives on the row it blocks, rather than
                    under the trigger where it used to sit on every row of a
                    full workspace. It explains one unavailable choice, so it
                    belongs beside that choice.
                  */}
                  <p className="text-xs text-muted-foreground">
                    {blocked ? 'No seats left.' : TIER_DESCRIPTIONS[option]}
                  </p>
                </div>
                {option === tier ? <Check className="size-4 shrink-0 text-brand" /> : null}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
